import type { TextareaHTMLAttributes } from "react";
import { cn } from "../lib/cn";

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "size"> {
	/**
	 * `md` — просторное поле витрины (r12, 20/16), `sm` — плотное поле админки
	 * (r8, 10/6). Ровно те же две геометрии, что у `Input`: многострочное поле
	 * стоит в тех же формах и обязано выглядеть его продолжением, а не соседом.
	 */
	size?: "md" | "sm";
	hasError?: boolean;
}

/**
 * Многострочное поле.
 *
 * Появилось в ките после того, как его дважды написали в приложениях:
 * `apps/admin/src/components/ui-local/textarea.tsx` (причина действия
 * оператора, `AdminReasonSchema`) и сырой `<textarea>` в
 * `apps/id-web/.../appeal-dialog.tsx` (текст обжалования). Копии разошлись не
 * только геометрией: у витрины фокус держался на одном брендовом гало, а бренд
 * даёт 1.5:1 к белому — с клавиатуры такое поле почти не видно. Здесь, как у
 * `Input` и `Select`, гало дополнено кольцом `focus-ring`.
 *
 * Текстовых умолчаний нет намеренно (`no-text-defaults.test.ts`): подпись,
 * подсказку и текст ошибки ставит приложение — кит встраивается и в чужие
 * сервисы экосистемы, где язык другой.
 */
export function Textarea({ size = "md", hasError, rows = 3, className, ...rest }: TextareaProps) {
	return (
		<textarea
			rows={rows}
			aria-invalid={hasError || undefined}
			className={cn(
				"w-full resize-y bg-sunken t-body text-fg outline-none",
				"border border-transparent transition-[border-color,box-shadow] duration-150 ease-smooth",
				"focus:border-brand focus:shadow-[0_0_0_3px_var(--color-ring-halo)]",
				"focus-ring",
				"disabled:cursor-not-allowed disabled:opacity-60 read-only:opacity-70",
				size === "sm" ? "rounded-field-sm px-2.5 py-1.5" : "rounded-card px-5 py-4",
				hasError && "border-danger",
				className,
			)}
			{...rest}
		/>
	);
}
